<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisPayloadViewer — safe, redacted JSON viewer for LAIS payloads.
 *
 * It is NEVER a raw `<pre>{{ payload }}</pre>`. Any key whose name matches
 * `redactedKeys` (case-insensitive, substring) is masked in the rendered tree
 * AND in the copied output, so secrets never reach the screen or the clipboard.
 * Nested objects/arrays collapse and expand; string payloads are parsed when
 * they are JSON and shown as plain text otherwise; null/empty renders a neutral
 * "no payload" state.
 */
const props = defineProps({
  payload: { type: [Object, Array, String, Number, Boolean], default: null },
  title: { type: String, default: '' },
  schemaVersion: { type: String, default: '' },
  redactedKeys: {
    type: Array,
    default: () => [
      'password',
      'token',
      'secret',
      'authorization',
      'cpf',
      'cnpj',
      'phone',
      'email',
      'document',
      'bank',
      'account',
      'pix',
    ],
  },
  canCopy: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: true },
  maxDepth: { type: Number, default: 3 },
});

const emit = defineEmits(['copy']);

const { t } = useI18n();

const MASK = '••••••';
const HARD_DEPTH_LIMIT = 24;

const instance = getCurrentInstance();
const baseId = `lais-payload-viewer-${instance?.uid ?? 'default'}`;
const titleId = `${baseId}-title`;
const treeId = `${baseId}-tree`;

const redactedKeyList = computed(() =>
  (Array.isArray(props.redactedKeys) ? props.redactedKeys : [])
    .map(key => String(key || '').toLowerCase())
    .filter(Boolean)
);

const matchesRedacted = key => {
  if (key === null || key === undefined) return false;
  const lowered = String(key).toLowerCase();
  return redactedKeyList.value.some(rule => lowered.includes(rule));
};

const typeOf = value => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const formatPrimitive = (type, value) => {
  if (type === 'string') return JSON.stringify(value);
  if (type === 'null' || value === null || value === undefined) return 'null';
  return String(value);
};

// Normalize props.payload into a render mode + value.
const normalized = computed(() => {
  const raw = props.payload;
  if (raw === null || raw === undefined) return { mode: 'empty', value: null };
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return { mode: 'empty', value: null };
    try {
      return { mode: 'tree', value: JSON.parse(trimmed) };
    } catch {
      return { mode: 'text', value: raw };
    }
  }
  return { mode: 'tree', value: raw };
});

const buildNode = (value, key, depth, id, seen) => {
  const type = typeOf(value);
  const redacted = matchesRedacted(key);
  const node = {
    id,
    key: key === null || key === undefined ? null : String(key),
    depth,
    type,
    redacted,
  };

  if (redacted) {
    node.kind = 'primitive';
    node.display = MASK;
    return node;
  }

  if (type === 'object' || type === 'array') {
    if (seen.has(value) || depth > HARD_DEPTH_LIMIT) {
      node.kind = 'primitive';
      node.display = '[…]';
      node.type = 'null';
      return node;
    }
    const nextSeen = new Set(seen);
    nextSeen.add(value);
    const entries =
      type === 'array'
        ? value.map((item, index) => [String(index), item])
        : Object.keys(value).map(childKey => [childKey, value[childKey]]);
    node.kind = 'container';
    node.containerType = type;
    node.size = entries.length;
    node.children = entries.map(([childKey, childValue], index) =>
      buildNode(childValue, childKey, depth + 1, `${id}/${index}`, nextSeen)
    );
    return node;
  }

  node.kind = 'primitive';
  node.display = formatPrimitive(type, value);
  return node;
};

const rootNode = computed(() =>
  normalized.value.mode === 'tree'
    ? buildNode(normalized.value.value, null, 0, 'root', new Set())
    : null
);

const collectContainers = (node, acc) => {
  if (node && node.kind === 'container') {
    acc.push(node.id);
    node.children.forEach(child => collectContainers(child, acc));
  }
  return acc;
};

const allContainerIds = computed(() =>
  rootNode.value ? collectContainers(rootNode.value, []) : []
);

const expandedPaths = ref(new Set());

const buildInitialExpanded = () => {
  const set = new Set();
  const depthLimit = props.collapsed ? 1 : Math.max(1, props.maxDepth);
  const walk = node => {
    if (node && node.kind === 'container') {
      if (node.depth < depthLimit) set.add(node.id);
      node.children.forEach(walk);
    }
  };
  if (rootNode.value) walk(rootNode.value);
  return set;
};

watch(
  [rootNode, () => props.collapsed, () => props.maxDepth],
  () => {
    expandedPaths.value = buildInitialExpanded();
  },
  { immediate: true }
);

const isExpanded = id => expandedPaths.value.has(id);

const toggleNode = node => {
  const next = new Set(expandedPaths.value);
  if (next.has(node.id)) next.delete(node.id);
  else next.add(node.id);
  expandedPaths.value = next;
};

const allExpanded = computed(
  () =>
    allContainerIds.value.length > 0 &&
    allContainerIds.value.every(id => expandedPaths.value.has(id))
);

const toggleAll = () => {
  expandedPaths.value = allExpanded.value
    ? new Set()
    : new Set(allContainerIds.value);
};

// Flatten the tree into visible rows honoring the current expand state.
const visibleRows = computed(() => {
  const rows = [];
  const walk = node => {
    rows.push(node);
    if (node.kind === 'container' && expandedPaths.value.has(node.id)) {
      node.children.forEach(walk);
    }
  };
  if (rootNode.value) walk(rootNode.value);
  return rows;
});

const containerMeta = node =>
  node.containerType === 'array' ? `[${node.size}]` : `{${node.size}}`;

const keyLabel = node => (node.key === null ? '' : `${node.key}: `);

const primitiveClass = node => {
  if (node.redacted) return 'text-lais-text-muted';
  if (node.type === 'number' || node.type === 'boolean')
    return 'text-lais-primary';
  if (node.type === 'null') return 'text-lais-text-muted';
  return 'text-lais-text-body';
};

const toggleLabel = node =>
  isExpanded(node.id)
    ? t('LAIS_PAYLOAD_VIEWER.COLLAPSE', {
        key: node.key ?? containerMeta(node),
      })
    : t('LAIS_PAYLOAD_VIEWER.EXPAND', { key: node.key ?? containerMeta(node) });

// Build a deeply redacted clone of the ORIGINAL value for copy output.
const redactValue = (value, key, seen) => {
  if (matchesRedacted(key)) return MASK;
  if (Array.isArray(value)) {
    if (seen.has(value)) return '[…]';
    const nextSeen = new Set(seen);
    nextSeen.add(value);
    return value.map(item => redactValue(item, null, nextSeen));
  }
  if (value && typeof value === 'object') {
    if (seen.has(value)) return '[…]';
    const nextSeen = new Set(seen);
    nextSeen.add(value);
    const out = {};
    Object.keys(value).forEach(childKey => {
      out[childKey] = redactValue(value[childKey], childKey, nextSeen);
    });
    return out;
  }
  return value;
};

const redactedString = computed(() => {
  const current = normalized.value;
  if (current.mode === 'empty') return '';
  if (current.mode === 'text') return current.value;
  try {
    return JSON.stringify(redactValue(current.value, null, new Set()), null, 2);
  } catch {
    return '';
  }
});

const justCopied = ref(false);
let copiedTimer;

const handleCopy = async () => {
  if (!props.canCopy) return;
  const text = redactedString.value;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    }
  } catch {
    // Clipboard is best-effort; the redacted payload is still emitted below.
  }
  justCopied.value = true;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    justCopied.value = false;
  }, 2000);
  emit('copy', text);
};

onBeforeUnmount(() => clearTimeout(copiedTimer));

const copyLabel = computed(() =>
  justCopied.value
    ? t('LAIS_PAYLOAD_VIEWER.COPIED')
    : t('LAIS_PAYLOAD_VIEWER.COPY')
);
const groupLabel = computed(() =>
  props.title ? undefined : t('LAIS_PAYLOAD_VIEWER.LANDMARK')
);
</script>

<template>
  <section
    class="flex flex-col overflow-hidden rounded-lais border border-lais-border-soft bg-lais-bg-card text-lais-text shadow-lais-card"
    :aria-labelledby="title ? titleId : undefined"
    :aria-label="groupLabel"
  >
    <header
      class="flex flex-wrap items-center gap-2 border-b border-lais-border-soft px-4 py-3"
    >
      <span
        class="i-lucide-braces size-4 shrink-0 text-lais-primary"
        aria-hidden="true"
      />
      <h3
        v-if="title"
        :id="titleId"
        class="min-w-0 truncate font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        {{ title }}
      </h3>
      <LaisStatusBadge
        v-if="schemaVersion"
        :label="schemaVersion"
        tone="neutral"
        size="sm"
        icon="i-lucide-git-commit-horizontal"
        :title="t('LAIS_PAYLOAD_VIEWER.SCHEMA', { version: schemaVersion })"
        :aria-label="
          t('LAIS_PAYLOAD_VIEWER.SCHEMA', { version: schemaVersion })
        "
      />
      <div class="ms-auto flex items-center gap-1.5">
        <button
          v-if="allContainerIds.length"
          type="button"
          data-testid="toggle-all"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-lais border border-lais-border-soft bg-lais-bg-soft px-2.5 py-1 text-lais-label-sm font-semibold text-lais-text-body transition-colors hover:border-lais-primary hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :aria-expanded="allExpanded"
          :aria-controls="treeId"
          @click="toggleAll"
        >
          <span
            class="size-3.5 shrink-0"
            :class="
              allExpanded
                ? 'i-lucide-chevrons-down-up'
                : 'i-lucide-chevrons-up-down'
            "
            aria-hidden="true"
          />
          <span>{{
            allExpanded
              ? t('LAIS_PAYLOAD_VIEWER.COLLAPSE_ALL')
              : t('LAIS_PAYLOAD_VIEWER.EXPAND_ALL')
          }}</span>
        </button>
        <button
          v-if="canCopy"
          type="button"
          data-testid="copy"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-lais border border-lais-border-soft bg-lais-bg-card px-2.5 py-1 text-lais-label-sm font-semibold text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
          :class="{
            'border-lais-primary bg-lais-primary-50 text-lais-primary':
              justCopied,
          }"
          :aria-label="t('LAIS_PAYLOAD_VIEWER.COPY_ARIA')"
          @click="handleCopy"
        >
          <span
            class="size-3.5 shrink-0"
            :class="justCopied ? 'i-lucide-check' : 'i-lucide-copy'"
            aria-hidden="true"
          />
          <span>{{ copyLabel }}</span>
        </button>
      </div>
    </header>

    <div
      class="max-h-[420px] overflow-auto px-4 py-3 font-mono text-lais-body-sm leading-relaxed"
    >
      <p
        v-if="normalized.mode === 'empty'"
        data-testid="empty"
        class="flex items-center gap-2 rounded-lais bg-lais-bg-soft px-3 py-4 font-redHatText text-lais-body-sm text-lais-text-muted"
      >
        <span class="i-lucide-file-x size-4 shrink-0" aria-hidden="true" />
        {{ t('LAIS_PAYLOAD_VIEWER.EMPTY') }}
      </p>

      <pre
        v-else-if="normalized.mode === 'text'"
        data-testid="text"
        class="whitespace-pre-wrap break-words text-lais-text-body"
        >{{ normalized.value }}</pre
      >

      <ul
        v-else
        :id="treeId"
        role="tree"
        :aria-label="t('LAIS_PAYLOAD_VIEWER.TREE_LABEL')"
        class="m-0 list-none p-0"
      >
        <li
          v-for="node in visibleRows"
          :key="node.id"
          role="treeitem"
          :aria-level="node.depth + 1"
          :aria-expanded="
            node.kind === 'container' ? isExpanded(node.id) : undefined
          "
          class="flex items-start gap-1 rounded-lais py-0.5"
          :style="{ paddingInlineStart: `${node.depth * 1.25}rem` }"
        >
          <button
            v-if="node.kind === 'container'"
            type="button"
            :data-testid="`toggle-${node.id}`"
            class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-lais text-lais-text-muted transition-colors hover:bg-lais-primary-50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
            :aria-label="toggleLabel(node)"
            :aria-expanded="isExpanded(node.id)"
            @click="toggleNode(node)"
          >
            <span
              class="i-lucide-chevron-right size-3.5 transition-transform"
              :class="isExpanded(node.id) ? 'rotate-90' : ''"
              aria-hidden="true"
            />
          </button>
          <span v-else class="mt-0.5 size-5 shrink-0" aria-hidden="true" />

          <span class="min-w-0 break-words">
            <span
              v-if="node.key !== null"
              class="font-semibold text-lais-text-strong"
              >{{ keyLabel(node) }}</span
            >
            <span
              v-if="node.kind === 'container'"
              class="text-lais-text-muted"
              >{{ containerMeta(node) }}</span
            >
            <span
              v-else-if="node.redacted"
              class="inline-flex items-center gap-1 align-middle text-lais-text-muted"
              :title="t('LAIS_PAYLOAD_VIEWER.REDACTED')"
            >
              <span class="i-lucide-lock size-3 shrink-0" aria-hidden="true" />
              <span aria-hidden="true">{{ MASK }}</span>
              <span class="sr-only">{{
                t('LAIS_PAYLOAD_VIEWER.REDACTED')
              }}</span>
            </span>
            <span v-else :class="primitiveClass(node)">{{ node.display }}</span>
          </span>
        </li>
      </ul>
    </div>

    <span class="sr-only" aria-live="polite" aria-atomic="true">
      {{ justCopied ? t('LAIS_PAYLOAD_VIEWER.COPIED') : '' }}
    </span>
  </section>
</template>
